import type { NetworkUnit } from "@/services/types"
import { cn } from "@/shared/lib/cn"
import { ClockSm, GlobeSm, MailSm, MapPinSm, PhoneSm } from "@/shared/ui/icons"

/** Linha de metadado com icone alinhado ao topo do texto. */
function MetaLine({ icon, className, children }: { icon: React.ReactNode; className?: string; children: React.ReactNode }) {
  return (
    <p className={cn("flex items-start gap-1.5", className)}>
      <span className="shrink-0 mt-0.5 text-verde-profundo/30">{icon}</span>
      {children}
    </p>
  )
}

function formatAddress(unit: NetworkUnit): string {
  const street = unit.district ? `${unit.address}, ${unit.district}` : unit.address
  return `${street} — ${unit.city}, ${unit.state} · ${unit.postalCode}`
}

export function NetworkUnitCard({ unit }: { unit: NetworkUnit }) {
  const isClinic = unit.kind === "PJ"

  return (
    <article className="flex flex-col gap-3 p-5 bg-white rounded-2xl border border-verde-profundo/10 shadow-sm hover:shadow-md hover:border-verde-profundo/20 transition-all">
      <div>
        <span
          className={cn(
            "inline-block text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full mb-1.5",
            isClinic ? "bg-verde-profundo/8 text-verde-profundo/60" : "bg-dourado/10 text-dourado-escuro",
          )}
        >
          {isClinic ? "Clínica" : "Profissional autônomo"}
        </span>

        <h3 className="font-semibold text-verde-profundo leading-snug text-sm">{unit.name}</h3>
        <p className="text-xs text-verde-profundo/40 mt-0.5">CRMV {unit.crmv}</p>
      </div>

      <ul className="flex flex-wrap gap-1.5">
        {unit.specialties.map((specialty) => (
          <li
            key={specialty}
            className="px-2 py-0.5 rounded-full bg-off-white text-verde-profundo/60 text-[11px] font-medium"
          >
            {specialty}
          </li>
        ))}
      </ul>

      <div className="flex flex-col gap-1.5 text-xs text-verde-profundo/50 border-t border-verde-profundo/6 pt-3">
        <MetaLine icon={<MapPinSm />}>{formatAddress(unit)}</MetaLine>
        <MetaLine icon={<ClockSm />}>{unit.openingHours}</MetaLine>

        {/* Uma unica regiao ja aparece no endereco; repeti-la seria ruido. */}
        {unit.servedRegions.length > 1 && (
          <MetaLine icon={<GlobeSm />} className="text-verde-profundo/38">
            Regiões: {unit.servedRegions.join(" · ")}
          </MetaLine>
        )}
      </div>

      {(unit.phone || unit.email) && (
        <div className="flex gap-2">
          {unit.email && (
            <a
              href={`mailto:${unit.email}`}
              className="flex-1 h-8 rounded-xl border border-verde-profundo/12 text-verde-profundo/50 text-[11px] font-medium flex items-center justify-center gap-1.5 hover:bg-off-white transition-colors"
            >
              <MailSm />
              E-mail
            </a>
          )}

          {unit.phone && (
            <a
              href={`tel:${unit.phone.replace(/\D/g, "")}`}
              className="flex-1 h-8 rounded-xl bg-verde-profundo text-white text-[11px] font-medium flex items-center justify-center gap-1.5 hover:bg-verde-medio transition-colors"
            >
              <PhoneSm />
              Ligar
            </a>
          )}
        </div>
      )}
    </article>
  )
}
